import React, { useState, useCallback, useEffect, useRef } from 'react';
import ActualsTab from './tabs/ActualsTab.jsx';
import BudgetsTab from './tabs/BudgetsTab.jsx';
import ForecastsTab from './tabs/ForecastsTab.jsx';
import SupportStackTab from './tabs/SupportStackTab.jsx';
import CashMonitorTab from './tabs/CashMonitorTab.jsx';
import SettingsTab from './tabs/SettingsTab.jsx';
import SynthesisTab from './tabs/SynthesisTab.jsx';
import FCCTab from './tabs/FCCTab.jsx';
import Sidebar, { SIDEBAR_ITEMS, parseRoute } from './components/Sidebar.jsx';
import Topbar from './components/Topbar.jsx';
import ScenarioBar from './components/ScenarioBar.jsx';
import ShortcutHelp from './components/ShortcutHelp.jsx';
import IntroPanel from './components/IntroPanel.jsx';
// E-023 FAB SearchBar — federated search across registered Lens sources.
// Phase 4 starter: lens.scenarios is the only source for now (PRESETS +
// localStorage profiles); cross-app federation to Ledger /api/search is
// queued for a later session.
import LensSearchBar from './components/LensSearchBar.jsx';
import { ToastHost } from './components/Toast.jsx';
import { GuidesProvider } from './components/GuidesContext.jsx';
import { PRESETS, DASHBOARD_VERSION, SNAPSHOT_AS_OF } from './data.js';
import { DEFAULT_PIECEWISE_LABELS } from './content/piecewise-starters.js';
// Phase 1.5 — the vendor transaction cache bootstrap was removed.
// Transaction drill-in will be wired from @cleic/ledger in a later phase.
import { getCleicReleaseInfo } from './release.js';
import {
  fetchLensBookScopeCatalog,
  getActiveLensBookScopeSelection,
  getLedgerActualsSnapshot,
  getLedgerSource,
  getLedgerSourceState,
  loadMonthlyLedger,
  setActiveLensBookScopeSelection,
} from './data-ledger.js';
import { SOURCE_STATE_CLASSES, formatSourceStateLabel, normalizeSourceState } from './source-state.js';
import { canonicalizeActiveTab, canonicalizePathname, pathnameToActiveTab, activeTabToPathname, DEFAULT_URL } from './routing.js';
import {
  loadStoredPresentationSettings,
  normalizePresentationSettings,
  setLensPresentationSettings,
} from './formatting.js';
import { readLedgerContextFromBrowser } from './ledger-handoff.js';

const TAB_IDS = SIDEBAR_ITEMS.map(t => t.id);
const PROFILE_STORAGE_KEY    = 'cma-dashboard-profiles-v7';
const VISIBILITY_STORAGE_KEY = 'cma-dashboard-visibility-v7_1';

function loadProfilesFromStorage() {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}

function loadVisibilityFromStorage() {
  try {
    const raw = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (!raw) return { accountVisibility: {}, accountTierFilter: 'primarySecondary', perTabTierFilter: {} };
    const parsed = JSON.parse(raw);
    return {
      accountVisibility: parsed?.accountVisibility ?? {},
      accountTierFilter: parsed?.accountTierFilter ?? 'primarySecondary',
      perTabTierFilter:  parsed?.perTabTierFilter  ?? {},
    };
  } catch { return { accountVisibility: {}, accountTierFilter: 'primarySecondary', perTabTierFilter: {} }; }
}

function persistVisibility(scenario) {
  try {
    localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify({
      accountVisibility: scenario.accountVisibility ?? {},
      accountTierFilter: scenario.accountTierFilter ?? 'primarySecondary',
      perTabTierFilter:  scenario.perTabTierFilter  ?? {},
    }));
  } catch { /* swallow */ }
}

/* v8 Path F — SESSION-ONLY PERSISTENCE MODEL
   Per the "FCC is a command center, not a ledger" principle: any change
   to scenario state (transactions, journal entries, slider values, etc.)
   lives in memory only. The ONLY durable path is an explicit Scenario
   Profile save. Reloading the app always returns to defaults + whatever
   named profile the user loads — no silent carryover.

   Kept localStorage (UI-preference tier, not scenario state):
     - scenario profiles themselves (named user saves — explicit)
     - sidebar collapse, formal-statement open/close, density/period
     - account visibility / tier filter (UI toggles, not values)

   Dropped localStorage (was v7.2–v7.3, now session-only):
     - assetTransactions (FCC buy/sell simulations)
     - adjustingEntries (Actuals journal entry drafts + posted)

   One-time cleanup removes the stale keys so old carryover is erased. */
const ASSET_TXN_STORAGE_KEY = 'cma-dashboard-asset-transactions-v7_2';
const ADJ_ENTRIES_STORAGE_KEY = 'cma-dashboard-adjusting-entries-v7_2';
function clearDeprecatedScenarioKeys() {
  try {
    localStorage.removeItem(ASSET_TXN_STORAGE_KEY);
    localStorage.removeItem(ADJ_ENTRIES_STORAGE_KEY);
  } catch { /* swallow */ }
}

/* v7.3.1 — per-tab formal-statement open/close preferences */
const FORMAL_STMTS_STORAGE_KEY = 'cma-dashboard-formal-stmts-v7_3_1';
const DEFAULT_FORMAL_STMTS_OPEN = {
  actuals:   { is: true,  bs: true,  cf: false },
  budgets:   { is: true,  bs: false, cf: false },
  forecasts: { is: false, bs: false, cf: true  },
  synthesis: { is: true,  bs: true,  cf: true  },
  fcc:       { is: false, bs: true,  cf: false },
};
function loadFormalStmtsFromStorage() {
  try {
    const raw = localStorage.getItem(FORMAL_STMTS_STORAGE_KEY);
    if (!raw) return DEFAULT_FORMAL_STMTS_OPEN;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? { ...DEFAULT_FORMAL_STMTS_OPEN, ...parsed } : DEFAULT_FORMAL_STMTS_OPEN;
  } catch { return DEFAULT_FORMAL_STMTS_OPEN; }
}
function persistFormalStmts(scenario) {
  try {
    localStorage.setItem(FORMAL_STMTS_STORAGE_KEY, JSON.stringify(scenario.formalStatementsOpen ?? DEFAULT_FORMAL_STMTS_OPEN));
  } catch { /* swallow */ }
}

/* v8 — GLOBAL period count + density (was per-tab in v7.4). Moving to
   scalar values driven by a single picker in the Topbar. All tabs read
   the same number. Legacy per-tab maps in localStorage are migrated on
   load by taking the max value (or default). */
const FORMAL_PERIODS_STORAGE_KEY = 'cma-dashboard-formal-periods-v7_4';
const DEFAULT_FORMAL_PERIOD_COUNT = 12;
function loadFormalPeriodsFromStorage() {
  try {
    const raw = localStorage.getItem(FORMAL_PERIODS_STORAGE_KEY);
    if (!raw) return DEFAULT_FORMAL_PERIOD_COUNT;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'number' && Number.isFinite(parsed)) return parsed;
    /* legacy per-tab map — migrate to a single scalar */
    if (parsed && typeof parsed === 'object') {
      const values = Object.values(parsed).filter(v => typeof v === 'number');
      return values.length ? Math.max(...values) : DEFAULT_FORMAL_PERIOD_COUNT;
    }
    return DEFAULT_FORMAL_PERIOD_COUNT;
  } catch { return DEFAULT_FORMAL_PERIOD_COUNT; }
}
function persistFormalPeriods(scenario) {
  try {
    localStorage.setItem(FORMAL_PERIODS_STORAGE_KEY, JSON.stringify(scenario.formalPeriodCount ?? DEFAULT_FORMAL_PERIOD_COUNT));
  } catch { /* swallow */ }
}

const FORMAL_DENSITY_STORAGE_KEY = 'cma-dashboard-formal-density-v8';
const DEFAULT_FORMAL_PERIOD_DENSITY = 1;
function loadFormalDensityFromStorage() {
  try {
    const raw = localStorage.getItem(FORMAL_DENSITY_STORAGE_KEY);
    if (!raw) return DEFAULT_FORMAL_PERIOD_DENSITY;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'number' && Number.isFinite(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') {
      const values = Object.values(parsed).filter(v => typeof v === 'number');
      return values.length ? Math.max(...values) : DEFAULT_FORMAL_PERIOD_DENSITY;
    }
    return DEFAULT_FORMAL_PERIOD_DENSITY;
  } catch { return DEFAULT_FORMAL_PERIOD_DENSITY; }
}
function persistFormalDensity(scenario) {
  try {
    localStorage.setItem(FORMAL_DENSITY_STORAGE_KEY, JSON.stringify(scenario.formalPeriodDensity ?? DEFAULT_FORMAL_PERIOD_DENSITY));
  } catch { /* swallow */ }
}

const ACTUALS_DATE_RANGE_STORAGE_KEY = 'cleic.lens.actualsDateRange.v1';

function todayLocalISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function startOfYearISO() {
  return `${new Date().getFullYear()}-01-01`;
}

function monthEndISO(ym) {
  const [year, month] = String(ym || '').split('-').map(Number);
  if (!year || !month) return todayLocalISO();
  const date = new Date(year, month, 0);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function normalizeDateOnly(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null;
}

function clampDate(value, min, max) {
  const date = normalizeDateOnly(value) || min;
  if (min && date < min) return min;
  if (max && date > max) return max;
  return date;
}

function normalizeActualsDateRange(input = {}, fallback = null) {
  const base = fallback || {
    minDate: startOfYearISO(),
    maxDate: todayLocalISO(),
    startDate: startOfYearISO(),
    endDate: todayLocalISO(),
  };
  const minDate = normalizeDateOnly(input.minDate) || base.minDate;
  const maxDate = normalizeDateOnly(input.maxDate) || base.maxDate;
  const safeMin = minDate <= maxDate ? minDate : maxDate;
  const safeMax = maxDate >= minDate ? maxDate : minDate;
  let startDate = clampDate(input.startDate || base.startDate, safeMin, safeMax);
  let endDate = clampDate(input.endDate || base.endDate, safeMin, safeMax);
  if (startDate > endDate) [startDate, endDate] = [endDate, startDate];
  return { minDate: safeMin, maxDate: safeMax, startDate, endDate };
}

function loadActualsDateRangeFromStorage() {
  const ledgerHandoff = readLedgerContextFromBrowser();
  const hasDateHandoff = Boolean(ledgerHandoff.reportStartDate || ledgerHandoff.reportEndDate);
  try {
    const raw = localStorage.getItem(ACTUALS_DATE_RANGE_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : {};
    const base = normalizeActualsDateRange(stored);
    if (!hasDateHandoff) return base;
    const handoffStart = ledgerHandoff.reportStartDate || base.startDate;
    const handoffEnd = ledgerHandoff.reportEndDate || base.endDate;
    return normalizeActualsDateRange(
      {
        minDate: handoffStart < base.minDate ? handoffStart : base.minDate,
        maxDate: handoffEnd > base.maxDate ? handoffEnd : base.maxDate,
        startDate: handoffStart,
        endDate: handoffEnd,
      },
      base,
    );
  } catch {
    if (!hasDateHandoff) return normalizeActualsDateRange();
    return normalizeActualsDateRange({
      minDate: ledgerHandoff.reportStartDate || startOfYearISO(),
      maxDate: ledgerHandoff.reportEndDate || todayLocalISO(),
      startDate: ledgerHandoff.reportStartDate || startOfYearISO(),
      endDate: ledgerHandoff.reportEndDate || todayLocalISO(),
    });
  }
}

function persistActualsDateRange(range) {
  try {
    localStorage.setItem(ACTUALS_DATE_RANGE_STORAGE_KEY, JSON.stringify(range));
  } catch { /* swallow */ }
}

function actualsDateRangeFromRollups(rollups, currentRange) {
  if (!Array.isArray(rollups) || rollups.length === 0) return currentRange;
  const months = rollups
    .map((row) => String(row?.ym || '').slice(0, 7))
    .filter((ym) => /^\d{4}-\d{2}$/.test(ym))
    .sort();
  if (months.length === 0) return currentRange;
  return normalizeActualsDateRange(
    {
      ...currentRange,
      minDate: `${months[0]}-01`,
      maxDate: monthEndISO(months[months.length - 1]),
    },
    currentRange,
  );
}

function LensNoScopeZeroState({ moduleId }) {
  const zeroCards = [
    ['Assets', '0.00'],
    ['Liabilities', '0.00'],
    ['Income', '0.00'],
    ['Cash flow', '0.00'],
  ];
  return (
    <section
      data-ux-pattern="lens-zero-scope-shell"
      className="rounded-lg border border-border bg-surface-raised p-5 text-text shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-dim">
            Book not connected
          </p>
          <h2 className="mt-1 text-lg font-semibold text-text">
            Zero shell
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-muted">
            {moduleId} is waiting for a Book or Shelf scope. No bundled demo, snapshot, or synthetic actuals are loaded.
          </p>
        </div>
        <span className="rounded-full border border-border bg-surface-el px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-text-muted">
          0 source rows
        </span>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {zeroCards.map(([label, value]) => (
          <div key={label} className="rounded-md border border-border bg-surface px-4 py-3">
            <p className="m-0 text-[0.66rem] uppercase tracking-[0.06em] text-text-dim">{label}</p>
            <p className="mt-2 font-mono text-xl font-semibold tabular-nums text-text">{value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function LensLedgerScopeHoldState({ moduleId, sourceState }) {
  const reason = sourceState?.fallbackReason || 'Ledger contract actuals are not ready for the selected scope.';
  return (
    <section
      data-ux-pattern="lens-scope-ledger-hold-shell"
      className="rounded-lg border border-border bg-surface-raised p-5 text-text shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-dim">
            Ledger scope selected
          </p>
          <h2 className="mt-1 text-lg font-semibold text-text">
            Waiting on Ledger actuals
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-muted">
            {moduleId} is held until the selected Book or Shelf emits Ledger-derived actuals. Static demo and synthetic rows stay blocked.
          </p>
        </div>
        <span className="rounded-full border border-border bg-surface-el px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-text-muted">
          HOLD
        </span>
      </div>
      <p className="mt-4 text-[0.80rem] text-text-dim">{reason}</p>
    </section>
  );
}

export default function App() {
  /* Proposal 7 — URL routing (partial, 2026-04-24).
     activeTab is the source-of-truth for WHAT is rendered; the URL mirrors it.
     On mount we hydrate activeTab from window.location.pathname; on change we
     pushState so the URL reflects the view. A popstate listener keeps the
     state in sync when the user hits the browser back/forward buttons. */
  const [activeTab, setActiveTabRaw] = useState(() => {
    if (typeof window === 'undefined') return 'actuals';
    const path = window.location.pathname;
    /* Proposal 3 (2026-04-25) — Journal Entries moved from Actuals to FCC.
       Legacy bookmarks redirect to the new home so muscle-memory URLs
       still work. replaceState happens here (lazy initializer is the
       only React-clean place to do it before first render). */
    if (path === '/actuals/je-list' || path === '/actuals/je-new') {
      window.history.replaceState(null, '', '/fcc/journal-entries');
      return 'fcc:journal-entries';
    }
    const canonicalPath = canonicalizePathname(path);
    if (canonicalPath !== path) window.history.replaceState(null, '', canonicalPath);
    return pathnameToActiveTab(path);
  });

  const setActiveTab = useCallback((tab) => {
    const canonicalTab = canonicalizeActiveTab(tab);
    setActiveTabRaw(canonicalTab);
    if (typeof window === 'undefined') return;
    const nextPath = activeTabToPathname(canonicalTab);
    if (window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
  }, []);

  // On mount: if the URL is bare "/" (default landing), replaceState to the
  // canonical default URL so refresh/bookmark always land consistently.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname === '/') {
      window.history.replaceState(null, '', DEFAULT_URL);
    }
  }, []);

  // Browser back/forward button handling.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const path = window.location.pathname;
      const canonicalPath = canonicalizePathname(path);
      if (canonicalPath !== path) window.history.replaceState(null, '', canonicalPath);
      setActiveTabRaw(pathnameToActiveTab(canonicalPath));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  /* Sidebar has two separate states so viewport changes don't clobber the
     user's explicit toggle:
       - userPreferredCollapsed: what the user explicitly chose (toggle/key)
       - sidebarCollapsed: the effective rendered state (may be forced by
         narrow viewports regardless of preference)
     When the viewport widens back, we restore userPreferredCollapsed. */
  const [userPreferredCollapsed, setUserPreferredCollapsed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((curr) => {
      const next = !curr;
      setUserPreferredCollapsed(next);   // remember explicit choice
      return next;
    });
  }, []);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookScopeSelection, setBookScopeSelection] = useState(() => getActiveLensBookScopeSelection());
  const [bookScopeCatalogState, setBookScopeCatalogState] = useState({
    data: null,
    loading: false,
    error: null,
  });
  const [actualsDateRange, setActualsDateRangeState] = useState(() => loadActualsDateRangeFromStorage());
  const ledgerReloadSeq = useRef(0);
  const [ledgerRefreshing, setLedgerRefreshing] = useState(false);
  const [scenario, setScenario] = useState(() => {
    const vis = loadVisibilityFromStorage();
    return {
      ...PRESETS.base,
      reportingDateRange: actualsDateRange,
      _actualsDateRange: actualsDateRange,
      _ledgerHandoffContext: readLedgerContextFromBrowser(),
      jmLoanRepaySchedule: null,
      piecewiseOverrides: {},
      tableOrientation: 'horizontal',
      statementStyle: 'analytical',
      comparisonMode: 'none',
      presentationSettings: normalizePresentationSettings(loadStoredPresentationSettings()),
      sectionDensity: {},                                    // v7 — per-section density
      profiles: loadProfilesFromStorage(),                   // v7 — saveable named scenarios
      activeProfile: null,
      eventOverrides: {},                                    // v7 — per-occurrence overrides
      accountVisibility: vis.accountVisibility,              // v7.1 — per-account visibility override
      accountTierFilter: vis.accountTierFilter,              // v7.1 — global tier filter
      perTabTierFilter:  vis.perTabTierFilter,               // v7.3 — per-tab tier override map
      fxRates: {},                                           // v7.1 — manual FX overrides
      multipliers: { marian: 1.0 },                          // v7.1 — dedicated routed-channel multiplier
      recurringOverrides: {},                                // v7.2 — per-line schedule overrides keyed by line.code
      piecewiseAssets: {},                                   // v8 Path AL — {assetId: [{ym, value, note}]} fixed-asset value pins per month
      piecewiseLTLs: {},                                     // v8 Path AL — {ltlId: [{ym, outstanding, note}]} LTL outstanding-balance pins per month
      piecewiseLabels: { ...DEFAULT_PIECEWISE_LABELS },     // Proposal 5B — user-configurable labels for the 4 engine kinds. Defaults to universal starter labels; user re-labels via Settings → Piecewise.
      assetTransactions: [],                                 // v8 — session-only (saved to profile when user wants durability)
      zbBannerHidden: false,                                 // v7.2 — session-scoped banner dismissal
      zbGapsAcknowledged: {},                                // v7.2 — per-code "mark as intentional" ack
      adjustingEntries: [],                                  // v8 — session-only, same rationale as assetTransactions
      includeAdjustments: true,                              // v7.3 — JE toggle (ON by default)
      formalStatementsOpen: loadFormalStmtsFromStorage(),    // v7.3.1 — per-tab IS/BS/CF collapsed state
      formalPeriodCount: loadFormalPeriodsFromStorage(),     // v7.4 — per-tab comparative period count
      formalPeriodDensity: loadFormalDensityFromStorage(),   // v8 — per-tab density (months per period)
      sparklineGradient: false,                              // v8 — toggle area-fill under row sparklines
      /* v8 Path P — per-module settings shape. Features default to `true`;
         users opt out. Persona mapping happens via `scenario.verbosity` +
         `moduleSettings.{module}.features` combined. Full contract in
         3-resources/canon/patterns/P-per-module-settings.md. */
      moduleSettings: {
        actuals:   { features: { balanceSheetDetail: true, incomeStatement: true, primaryIncome: true, formalStatements: true } },
        budgets:   { features: { financialRatios: true, ratiosTimeline: true, recurringTable: true, familyComposition: true, workingCapital: true, arStreams: true, formalStatements: true }, preferences: { ratioBenchmarkSet: 'ifrs-global' } },
        forecasts: { features: { drivers: true, overlays: true, comparison: true, scenario: true, formalStatements: true } },
        'support-stack': { features: { subLedgers: true, schedules: true, trackers: true, writeIntents: true } },
        'cash-monitor': { features: { dailyPlan: true, receipts: true, obligations: true, scenarios: true, warnings: true } },
        synthesis: { features: { trajectory: true, variance: true, tornado: true, contribution: true, formalStatements: true } },
        fcc:       { features: { liquid: true, fixedAssets: true, journalEntries: true, transactions: true, threeStmt: true } },
      },
    };
  });

  /* v7.1 — persist account visibility map every time it changes (incl. v7.3 per-tab overrides) */
  useEffect(() => {
    persistVisibility(scenario);
  }, [scenario.accountVisibility, scenario.accountTierFilter, scenario.perTabTierFilter]);

  /* v8 Path F — asset transactions + adjusting entries are session-only.
     Scenario profiles are the ONE durable save path. On mount, erase
     any stale localStorage carryover from pre-v8 sessions. */
  useEffect(() => {
    clearDeprecatedScenarioKeys();
  }, []);

  /* v7.3.1 — persist formal-statement open/close state */
  useEffect(() => {
    persistFormalStmts(scenario);
  }, [scenario.formalStatementsOpen]);

  /* v7.4 — persist per-tab comparative period count */
  useEffect(() => {
    persistFormalPeriods(scenario);
  }, [scenario.formalPeriodCount]);

  /* v8 — persist per-tab period density */
  useEffect(() => {
    persistFormalDensity(scenario);
  }, [scenario.formalPeriodDensity]);

  useEffect(() => {
    setLensPresentationSettings(
      scenario.presentationSettings,
      scenario._ledgerSourceState?.bookSettings,
    );
  }, [scenario.presentationSettings, scenario._ledgerSourceState]);

  const applyActualsDateRange = useCallback((nextRange) => {
    setActualsDateRangeState((current) => {
      const normalized = normalizeActualsDateRange(nextRange, current);
      persistActualsDateRange(normalized);
      setScenario((s) => ({
        ...s,
        reportingDateRange: normalized,
        _actualsDateRange: normalized,
      }));
      return normalized;
    });
  }, []);

  const syncActualsDateRangeFromRollups = useCallback((rollups) => {
    if (!Array.isArray(rollups) || rollups.length === 0) return;
    setActualsDateRangeState((current) => {
      const normalized = actualsDateRangeFromRollups(rollups, current);
      persistActualsDateRange(normalized);
      setScenario((s) => ({
        ...s,
        reportingDateRange: normalized,
        _actualsDateRange: normalized,
      }));
      return normalized;
    });
  }, []);

  /* Phase 1.5 — the vendor transaction cache bootstrap was removed.
     The `_txCacheReady` scenario flag is preserved so future code can flip it
     when transaction drill-in gets re-wired through the atomic-ledger-contract. */

  useEffect(() => {
    let cancelled = false;
    setBookScopeCatalogState((state) => ({ ...state, loading: true, error: null }));
    fetchLensBookScopeCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setBookScopeCatalogState({ data: catalog, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setBookScopeCatalogState({
          data: null,
          loading: false,
          error: err?.message || 'Ledger Books/Shelves unavailable',
        });
      });
    return () => { cancelled = true; };
  }, []);

  const refreshLedgerActuals = useCallback(() => {
    setScenario((s) => ({
      ...s,
      _ledgerSource: getLedgerSource(),
      _ledgerSourceState: getLedgerSourceState(),
      _ledgerActuals: null,
      _ledgerRollups: [],
      _ledgerReady: false,
    }));

    const seq = ++ledgerReloadSeq.current;
    setLedgerRefreshing(true);
    loadMonthlyLedger({ force: true })
      .then((rollups) => {
        if (seq !== ledgerReloadSeq.current) return;
        syncActualsDateRangeFromRollups(rollups);
        setScenario((s) => ({
          ...s,
          _ledgerSource: getLedgerSource(),
          _ledgerSourceState: getLedgerSourceState(),
          _ledgerActuals: getLedgerActualsSnapshot(),
          _ledgerRollups: Array.isArray(rollups) ? rollups : [],
          _ledgerReady: true,
        }));
      })
      .catch((err) => {
        if (seq !== ledgerReloadSeq.current) return;
        console.warn('[App] monthly ledger reload failed:', err?.message || err);
        setScenario((s) => ({
          ...s,
          _ledgerSource: getLedgerSource(),
          _ledgerSourceState: normalizeSourceState({
            ...getLedgerSourceState(),
            sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
            fallbackReason: 'Lens monthly ledger reload failed',
            warning: 'Ledger-derived actuals failed to load for the selected scope. Static demo and synthetic rows remain blocked.',
          }),
          _ledgerActuals: null,
          _ledgerRollups: [],
          _ledgerReady: true,
        }));
      })
      .finally(() => {
        if (seq === ledgerReloadSeq.current) setLedgerRefreshing(false);
      });
  }, [syncActualsDateRangeFromRollups]);

  const handleBookScopeChange = useCallback((nextSelection) => {
    const applied = setActiveLensBookScopeSelection(nextSelection);
    setBookScopeSelection(applied);
    refreshLedgerActuals();
  }, [refreshLedgerActuals]);

  /* v8 Path D — bootstrap the monthly ledger cache. Tries /ledger.json
     first (LLM-generated from any accounting source), else derives from
     local historical source data. The result is stashed on window so
     engine.backCastActuals can read it synchronously. Scenario flag
     triggers a re-render once data is available. */
  useEffect(() => {
    let cancelled = false;
    loadMonthlyLedger()
      .then((rollups) => {
        if (cancelled) return;
        syncActualsDateRangeFromRollups(rollups);
        /* Cache is already published by data-ledger.js → ledger-cache.js.
           We just need to trigger a re-render so engine.backCastActuals
           picks it up on the next tab read. */
        setScenario(s => ({
          ...s,
          _ledgerSource: getLedgerSource(),
          _ledgerSourceState: getLedgerSourceState(),
          _ledgerActuals: getLedgerActualsSnapshot(),
          _ledgerRollups: Array.isArray(rollups) ? rollups : [],
          _ledgerReady: true,
        }));
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[App] monthly ledger load failed:', err?.message || err);
        setScenario(s => ({
          ...s,
          _ledgerSource: getLedgerSource(),
          _ledgerSourceState: normalizeSourceState({
            ...getLedgerSourceState(),
            sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
            fallbackReason: 'Lens monthly ledger load failed',
            warning: 'Ledger-derived actuals failed to load for the selected scope. Static demo and synthetic rows remain blocked.',
          }),
          _ledgerActuals: null,
          _ledgerRollups: [],
          _ledgerReady: true,
        }));
      });
    return () => { cancelled = true; };
  }, []);

  /* Responsive auto-collapse that RESTORES on widen.
     Narrow viewport forces collapsed=true; widening restores the user's
     last explicit preference (which defaults to false / expanded until
     they toggle). Fires on browser zoom in/out too — zoom changes
     innerWidth the same way a window resize does. */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(userPreferredCollapsed);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [userPreferredCollapsed]);

  /* E-023 — Ctrl/Cmd+K opens the federated SearchBar.
     Mounted as a SEPARATE listener (not folded into the chord handler below)
     because the chord handler explicitly bails on modifier keys to leave
     them for the browser. The SearchBar's own shortcut layer ALSO listens
     once the bar is open; we just need the open trigger here. */
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'k' && !e.shiftKey) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Keyboard shortcuts — chord (g + letter) and single-key (?, [) */
  const chordRef = useRef({ pending: null, timer: null });
  useEffect(() => {
    const clearChord = () => {
      if (chordRef.current.timer) clearTimeout(chordRef.current.timer);
      chordRef.current = { pending: null, timer: null };
    };
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === '?') { setHelpOpen(true); e.preventDefault(); return; }
      if (e.key === 'Escape') { setHelpOpen(false); clearChord(); return; }
      if (e.key === '[') { toggleSidebar(); e.preventDefault(); return; }

      if (chordRef.current.pending === 'g') {
        const map = { c: 'actuals', m: 'budgets', f: 'forecasts', u: 'support-stack', h: 'cash-monitor', s: 'synthesis', b: 'fcc', d: 'drivers', t: 'settings' };
        const target = map[e.key.toLowerCase()];
        if (target) { setActiveTab(target); e.preventDefault(); }
        clearChord();
        return;
      }
      if (e.key === 'g') {
        chordRef.current.pending = 'g';
        chordRef.current.timer = setTimeout(clearChord, 1200);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); clearChord(); };
  }, []);

  /* onJump — routing callback for MoneyCell registry refs.
     Accepts `{ tab, section }`. If `section` is provided and matches a
     child of `tab`, routes to focus-mode `${tab}:${section}`; otherwise
     falls back to module overview + in-page scroll. This supports both
     the original MoneyCell behaviour (scroll-within-module) and the
     Simulations Registry "Edit in X →" buttons (enter focus-mode). */
  const onJump = useCallback((ref) => {
    if (!ref?.tab) return;
    const target = String(ref.tab).toLowerCase();
    if (!TAB_IDS.includes(target)) return;

    if (ref.section) {
      const moduleItem = SIDEBAR_ITEMS.find(t => t.id === target);
      const hasChild = moduleItem?.children?.some(ch => ch.id === ref.section);
      if (hasChild) {
        setActiveTab(`${target}:${ref.section}`);
        return;
      }
    }

    setActiveTab(target);
    setTimeout(() => {
      if (ref.section) {
        const el = document.getElementById(`section-${ref.section}`)
                || document.querySelector(`[data-section="${ref.section}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  }, []);

  const shellCols = sidebarCollapsed
    ? 'grid-cols-[64px_1fr]'
    : 'grid-cols-[64px_1fr] lg:grid-cols-[240px_1fr]';
  const sourceState = scenario._ledgerSourceState;
  const releaseInfo = getCleicReleaseInfo();
  const hasExplicitLensScope = !!(bookScopeSelection?.bookId || bookScopeSelection?.shelfId);
  const hasScopedLedgerActuals = hasExplicitLensScope && !!scenario._ledgerActuals;

  return (
    <ToastHost>
    <GuidesProvider value={scenario?.guides}>
    <div className={`grid h-screen overflow-hidden transition-[grid-template-columns] duration-200 ease-out ${shellCols}`}>
      <Sidebar
        activeTab={activeTab}
        onSelect={setActiveTab}
        collapsed={sidebarCollapsed}
        releaseInfo={releaseInfo}
      />

      <div className="flex flex-col overflow-hidden min-w-0 bg-bg">
        <Topbar
          activeTab={activeTab}
          sourceState={sourceState}
          ledgerHandoffContext={scenario._ledgerHandoffContext}
          bookScopeCatalog={bookScopeCatalogState.data}
          bookScopeSelection={bookScopeSelection}
          bookScopeLoading={bookScopeCatalogState.loading}
          bookScopeError={bookScopeCatalogState.error}
          actualsDateRange={actualsDateRange}
          ledgerReady={scenario?._ledgerReady === true}
          ledgerRefreshing={ledgerRefreshing}
          onBookScopeChange={handleBookScopeChange}
          onActualsDateRangeChange={applyActualsDateRange}
          onRefreshLedger={refreshLedgerActuals}
          onToggleSidebar={toggleSidebar}
          onShowHelp={() => setHelpOpen(true)}
        />
        <ScenarioBar
          scenario={scenario}
          sourceState={sourceState}
          setScenario={setScenario}
          onJumpToSettings={() => setActiveTab('settings:profiles')}
          activeTab={(() => parseRoute(activeTab).moduleId)()}
        />

        {(() => {
          /* v8 Path E — parse `module:section` routes once per render so the
             sidebar highlights children and tabs receive an `activeSection`
             prop to drive internal sub-routing (Actuals) or scroll-to-anchor
             (others). The ScenarioBar still reads the plain moduleId. */
          const { moduleId, sectionId } = parseRoute(activeTab);
          return (
        <main className="flex-1 overflow-y-auto overflow-x-hidden pt-4 pb-8 px-6 w-full min-w-0">
          <div className="max-w-[1640px] mx-auto w-full min-w-0">
            {/* v8 — tab transition: fade-in wrapper keyed on activeTab so switching
                tabs replays the enter animation. CSS keyframes defined in index.css. */}
            <div key={activeTab} className="animate-fade-in">
            {/* Proposal 6 Tier 1 — dismissible module introduction. Renders
                only on module overviews (no activeSection). Per-module state
                persisted in localStorage. Auto-hides when dismissed. */}
            {!hasExplicitLensScope && moduleId === 'actuals' ? (
              <ActualsTab scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />
            ) : !hasExplicitLensScope ? (
              <LensNoScopeZeroState moduleId={moduleId} />
            ) : !hasScopedLedgerActuals && moduleId === 'actuals' ? (
              <ActualsTab scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />
            ) : !hasScopedLedgerActuals ? (
              <LensLedgerScopeHoldState moduleId={moduleId} sourceState={sourceState} />
            ) : (
              <>
                {!sectionId && <IntroPanel moduleId={moduleId} />}
                {moduleId === 'actuals'   && <ActualsTab   scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'budgets'   && <BudgetsTab   scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'forecasts' && <ForecastsTab scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'support-stack' && <SupportStackTab activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} onJump={onJump} />}
                {moduleId === 'cash-monitor' && <CashMonitorTab scenario={scenario} setScenario={setScenario} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'synthesis' && <SynthesisTab scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'fcc'       && <FCCTab       scenario={scenario} setScenario={setScenario} onJump={onJump} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} />}
                {moduleId === 'drivers'   && <SettingsTab  scenario={scenario} setScenario={setScenario} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} moduleMode="drivers" />}
                {moduleId === 'settings'  && <SettingsTab  scenario={scenario} setScenario={setScenario} activeSection={sectionId} onBackToOverview={() => setActiveTab(moduleId)} moduleMode="settings" />}
              </>
            )}
            </div>

            <footer className="mt-8 pt-5 border-t border-border text-text-muted text-[0.82rem] leading-relaxed flex gap-8 flex-wrap justify-between">
              <div>
                <strong className="text-text">v{DASHBOARD_VERSION.replace(/^v/, '')} · snapshot {SNAPSHOT_AS_OF}</strong>
                <p className="mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm">
                  UX overhaul · sidebar nav · density control · SYNTHESIS module · charts everywhere · profiles · context menus
                </p>
              </div>
              <div className="text-right [&_p]:mb-[0.3rem] [&_p:last-child]:mb-0">
                <p>Source state: <code>{formatSourceStateLabel(sourceState)}</code></p>
                <p>Local profiles and visible-row exports stay private until reviewed and redacted.</p>
                <p>Every output is a prayer. Every file handled with Love and Respect.</p>
              </div>
            </footer>
          </div>
        </main>
          );
        })()}
      </div>

      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      {/* E-023 FAB SearchBar — opens on Ctrl/Cmd+K. Selecting a scenario
          calls setScenario with a clone of the chosen preset/profile,
          mirroring the manual-pick path through ScenarioBar.profiles. */}
      <LensSearchBar
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectScenario={(rawScenario) => {
          if (!rawScenario || !rawScenario.scenario) return;
          // Apply the chosen scenario but PRESERVE per-session settings
          // (visibility, density, table orientation) the user has tuned —
          // mirroring ScenarioBar.applyProfile semantics.
          setScenario((current) => ({
            ...current,
            ...rawScenario.scenario,
            // keep the per-session UI prefs that aren't part of the scenario
            // numerical model
            tableOrientation: current.tableOrientation,
            statementStyle: current.statementStyle,
            presentationSettings: current.presentationSettings,
            sectionDensity: current.sectionDensity,
            accountVisibility: current.accountVisibility,
          }));
        }}
      />
    </div>
    </GuidesProvider>
    </ToastHost>
  );
}
